/**
 * DOM utility functions for general use.
 */

/**
 * Creates a DOM element with optional classes and attributes.
 *
 * @param {string} tag - The tag name for the element (e.g., 'div', 'input').
 * @param {Object} [options] - Options for the element.
 * @param {string[]} [options.classes] - Array of class names to add.
 * @param {Object} [options.attributes] - Object of attribute key-value pairs to set.
 * @returns {HTMLElement} The created DOM element.
 *
 * @example
 * // Create an input element with classes and attributes
 * const input = createDomElement('input', {
 *   classes: ['autocomplete-placeholder', 'js-autocomplete-placeholder'],
 *   attributes: {
 *     'aria-hidden': 'true',
 *     'readonly': 'readonly',
 *     'tabindex': '-1'
 *   }
 * });
 */
export default function createDomElement(tag, { classes = [], attributes = {} } = {}) {
    const el = document.createElement(tag);
    if (classes.length > 0) {
        el.className = classes.join(' ');
    }
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
    return el;
}

/**
 * Retrieves a data attribute value from a DOM element.
 *
 * @param {HTMLElement} element
 * @param {string} attr
 * @returns {string|undefined}
 */
export function getDataAttribute(element, attr) {
    return element?.dataset?.[attr];
}
